import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  hasPermission: (...perms: string[]) => boolean;
  hasAnyPermission: (...perms: string[]) => boolean;
  isOwner: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser && authService.isAuthenticated()) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  // Escucha el evento disparado por api.ts cuando el refresh devuelve permisos actualizados.
  // Actualiza el estado en vivo sin requerir logout/login.
  useEffect(() => {
    const handlePermissionsUpdated = (e: CustomEvent<User>) => {
      setUser(e.detail);
      toast('Tus permisos fueron actualizados por un administrador.', {
        icon: '🔑',
        duration: 5000,
      });
    };
    window.addEventListener('auth:permissions-updated', handlePermissionsUpdated as EventListener);
    return () => window.removeEventListener('auth:permissions-updated', handlePermissionsUpdated as EventListener);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      const userData: User = {
        id: response.user.id,
        email: response.user.email,
        roleId: response.user.roleId,
        roleName: response.user.roleName,
        name: response.user.name,
        permissions: response.user.permissions || [],
        isOwner: response.user.isOwner ?? false,
      };
      authService.saveSession(response.token, userData, response.refreshToken);
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Verifica que el usuario tenga TODOS los permisos indicados
  const hasPermission = (...perms: string[]): boolean => {
    if (!user) return false;
    if (user.isOwner) return true;
    return perms.every(p => user.permissions?.includes(p) ?? false);
  };

  // Verifica que el usuario tenga AL MENOS UNO de los permisos
  const hasAnyPermission = (...perms: string[]): boolean => {
    if (!user) return false;
    if (user.isOwner) return true;
    return perms.some(p => user.permissions?.includes(p) ?? false);
  };

  const isOwner = (): boolean => {
    return !!user?.isOwner;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    hasPermission,
    hasAnyPermission,
    isOwner,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};